import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Sparkles, RotateCcw, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { firstName } from "../lib/utils";
import { LogoMark } from "./BrandLogo";
import { Switch } from "./ui/switch";

const SID_KEY = "kesh_session_id";
const COOKIE_KEY = "gi_cookie_ack_v1";
const HIDE_KEY = "gi_kesh_launcher_hidden";

// Renders Kesh's plain-text replies: "- " lines become a list and **text** becomes bold.
function KeshText({ text }) {
  const bold = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part));
  const blocks = [];
  text.split("\n").forEach((line) => {
    const item = line.match(/^\s*[-*•]\s+(.*)/);
    if (item) {
      const last = blocks[blocks.length - 1];
      if (last?.type === "ul") last.items.push(item[1]); else blocks.push({ type: "ul", items: [item[1]] });
    } else if (line.trim()) blocks.push({ type: "p", text: line });
  });
  return blocks.map((b, i) => (b.type === "ul"
    ? <ul key={i} className="list-disc pl-5 space-y-1 my-1">{b.items.map((t, j) => <li key={j}>{bold(t)}</li>)}</ul>
    : <p key={i} className="my-1 first:mt-0 last:mb-0">{bold(b.text)}</p>));
}
const MODELS = [
  { id: "gpt", label: "GPT 5.6" },
  { id: "claude", label: "Claude" },
  { id: "gemini-flash", label: "Gemini Flash" },
];

function Bubble({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      {!mine && <span className="w-7 h-7 rounded-full bg-kesh text-onbrand flex items-center justify-center text-xs font-bold shrink-0 mr-2 mt-0.5" aria-hidden="true">K</span>}
      <div className={`max-w-[85%] min-w-0 break-words rounded-2xl px-3.5 py-2.5 text-[15px] leading-relaxed ${mine ? "bg-brand text-onbrand rounded-br-md whitespace-pre-wrap" : "bg-kesh-soft border border-kesh/15 text-ink rounded-tl-md"}`}>
        <span className="sr-only">{mine ? "You: " : "Kesh: "}</span>{mine ? m.content : <KeshText text={m.content || ""} />}
      </div>
    </div>
  );
}

export const KeshWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [model, setModel] = useState(() => localStorage.getItem("kesh_model") || "gpt");
  const [useData, setUseData] = useState(() => localStorage.getItem("kesh_use_data") !== "false");
  const { pathname } = useLocation();
  const inDash = pathname.startsWith("/dashboard");
  const endRef = useRef(null);
  const [bannerUp, setBannerUp] = useState(() => { try { return !localStorage.getItem(COOKIE_KEY); } catch { return false; } });
  const [ctx, setCtx] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(HIDE_KEY) === "1");
  const inputRef = useRef(null);
  const opener = useRef(null);
  const lastMsg = useRef("");
  const sendRef = useRef(null);
  const openRef = useRef(false);
  const queued = useRef(null);
  openRef.current = open;

  useEffect(() => {
    const openKesh = () => { opener.current = document.activeElement; setOpen(true); };
    const onCtx = (e) => setCtx(e.detail || null);
    const onAsk = (e) => {
      const q = e.detail?.message;
      if (!q) return;
      if (openRef.current) sendRef.current?.(q);
      else { queued.current = q; setOpen(true); }
    };
    window.addEventListener("kesh-open", openKesh);
    window.addEventListener("kesh-context", onCtx);
    window.addEventListener("kesh-ask", onAsk);
    return () => {
      window.removeEventListener("kesh-open", openKesh);
      window.removeEventListener("kesh-context", onCtx);
      window.removeEventListener("kesh-ask", onAsk);
    };
  }, []);

  useEffect(() => {
    const onConsent = () => setBannerUp(false);
    window.addEventListener("cookie-consent", onConsent);
    return () => window.removeEventListener("cookie-consent", onConsent);
  }, []);

  useEffect(() => {
    if (!open) return;
    const sid = localStorage.getItem(SID_KEY);
    api.get("/kesh/session", { params: sid ? { session_id: sid } : {} })
      .then(({ data }) => {
        if (!data.session_id) localStorage.removeItem(SID_KEY);
        setMessages(data.messages || []);
        setSuggestions(data.suggestions || []);
      })
      .catch(() => {})
      .finally(() => {
        const q = queued.current;
        queued.current = null;
        if (q) sendRef.current?.(q);
      });
  }, [open, user?.user_id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages, sending]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 50);
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    const small = window.matchMedia("(max-width: 639px)").matches;
    if (small) document.body.style.overflow = "hidden";
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); if (small) document.body.style.overflow = ""; };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  }, [input, open]);

  const close = () => {
    setOpen(false);
    const el = opener.current;
    opener.current = null;
    if (el && document.contains(el)) setTimeout(() => el.focus?.({ preventScroll: true }), 0);
  };
  const dismissLauncher = () => { setHidden(true); sessionStorage.setItem(HIDE_KEY, "1"); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setError("");
    lastMsg.current = msg;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await api.post("/kesh/chat", {
        message: msg, session_id: localStorage.getItem(SID_KEY), model, use_my_data: useData,
        page: (ctx?.page || "").slice(0, 190), app_id: ctx?.appId || null,
      });
      localStorage.setItem(SID_KEY, data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => m.slice(0, -1));
      setError(e?.response?.data?.detail || "Kesh couldn't reply. Please try again.");
    } finally {
      setSending(false);
    }
  };
  sendRef.current = send;
  const shownSuggestions = ctx?.suggestions?.length ? ctx.suggestions : suggestions;

  const reset = () => {
    localStorage.removeItem(SID_KEY);
    setMessages([]);
    setError("");
  };

  return (
    <>
      {!open && !ctx?.inline && !hidden && pathname !== "/unsubscribe" && (
        <div data-testid="kesh-launcher" className={`fixed ${bannerUp ? "bottom-[220px] sm:bottom-5" : "bottom-4 sm:bottom-5"} ${inDash ? "max-md:hidden" : "max-sm:hidden"} right-4 sm:right-5 z-50 transition-[bottom] duration-200`}>
          <button data-testid="kesh-open-button" onClick={(e) => { opener.current = e.currentTarget; setOpen(true); }} aria-label="Chat with Kesh"
            className="flex items-center gap-2.5 p-3.5 sm:pl-2 sm:pr-5 sm:py-2 rounded-full bg-kesh text-onbrand font-semibold shadow-float hover:bg-kesh-hover hover:-translate-y-0.5 transition-[transform,background-color] duration-200">
            <span className="hidden sm:flex w-8 h-8 rounded-full bg-white/15 items-center justify-center text-sm font-bold" aria-hidden="true">K</span>
            <MessageCircle className="w-5 h-5 sm:hidden" aria-hidden="true" /> <span className="hidden sm:inline">Ask Kesh</span>
          </button>
          <button data-testid="kesh-launcher-dismiss" onClick={dismissLauncher} aria-label="Hide the Kesh button" title="Hide"
            className="absolute -top-4 -left-4 w-11 h-11 flex items-center justify-center group/x">
            <span className="w-6 h-6 rounded-full bg-surface border border-line text-body shadow-card flex items-center justify-center group-hover/x:text-ink group-hover/x:bg-subtle transition-colors"><X className="w-3.5 h-3.5" /></span>
          </button>
        </div>
      )}
      {open && (
        <div data-testid="kesh-panel" className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-[60] w-full sm:w-[400px] h-[100dvh] sm:h-[min(620px,calc(100dvh-2.5rem))] flex flex-col sm:rounded-2xl sm:border border-line bg-surface shadow-float overflow-hidden af-fade-up pt-safe" role="dialog" aria-modal="false" aria-label="Chat with Kesh">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-line border-t-[3px] border-t-kesh">
            <div className="relative w-9 h-9 rounded-full bg-kesh flex items-center justify-center font-display font-bold text-onbrand" aria-hidden="true">K<span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-success border-2 border-surface" /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-ink leading-tight">Kesh</div>
              <div className="text-xs text-kesh-text flex items-center gap-1.5 min-w-0" data-testid="kesh-brand-line">
                <LogoMark size={14} /> <span className="truncate">GetInterviewed career companion</span>
              </div>
            </div>
            <button data-testid="kesh-reset-button" onClick={reset} title="New chat" aria-label="Start a new chat" className="w-10 h-10 rounded-lg flex items-center justify-center text-body hover:text-ink hover:bg-subtle transition-colors"><RotateCcw className="w-4 h-4" /></button>
            <button data-testid="kesh-close-button" onClick={close} title="Close (Esc)" aria-label="Close Kesh" className="w-10 h-10 rounded-lg flex items-center justify-center text-ink bg-subtle hover:bg-line transition-colors"><X className="w-5 h-5" /></button>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2 border-b border-line text-xs text-body">
            <div className="flex gap-1" data-testid="kesh-model-picker">
              {MODELS.map((m) => (
                <button key={m.id} data-testid={`kesh-model-${m.id}`} onClick={() => { setModel(m.id); localStorage.setItem("kesh_model", m.id); }}
                  aria-pressed={model === m.id} className={`px-2.5 py-1 rounded-full border transition-colors ${model === m.id ? "border-kesh/40 bg-kesh-soft text-kesh-text font-semibold" : "border-line hover:border-kesh/40"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            {user && (
              <label className="flex items-center gap-2 ml-auto cursor-pointer" title="Use my CV & applications for personal answers">
                My data
                <Switch data-testid="kesh-use-data-toggle" checked={useData} onCheckedChange={(v) => { setUseData(v); localStorage.setItem("kesh_use_data", String(v)); }} />
              </label>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="kesh-messages">
            {messages.length === 0 && (
              <div className="text-sm text-body">
                <p className="mb-4 text-[15px] text-ink">Hi{firstName(user) ? ` ${firstName(user)}` : ""}! I'm Kesh. Ask me anything about GetInterviewed or your job search.</p>
                <div className="grid gap-2">
                  {shownSuggestions.map((s, i) => (
                    <button key={s} data-testid={`kesh-suggestion-${i}`} onClick={() => send(s)}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-line bg-surface text-left text-sm text-ink hover:border-kesh/40 hover:bg-kesh-soft transition-colors">
                      <Sparkles className="w-4 h-4 text-kesh shrink-0" aria-hidden="true" /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => <Bubble key={i} m={m} />)}
            {sending && (
              <div className="flex items-center gap-2" data-testid="kesh-typing" aria-live="polite"><span className="w-7 h-7 rounded-full bg-kesh text-onbrand flex items-center justify-center text-xs font-bold" aria-hidden="true">K</span><span className="flex gap-1 px-3.5 py-3 rounded-2xl rounded-tl-md bg-kesh-soft border border-kesh/15"><span className="gi-dot w-1.5 h-1.5 rounded-full bg-kesh" /><span className="gi-dot w-1.5 h-1.5 rounded-full bg-kesh" style={{ animationDelay: "0.15s" }} /><span className="gi-dot w-1.5 h-1.5 rounded-full bg-kesh" style={{ animationDelay: "0.3s" }} /></span><span className="sr-only">Kesh is typing</span></div>
            )}
            {error && (
              <div data-testid="kesh-error" role="alert" className="text-sm text-ink bg-danger-soft border border-danger/20 rounded-lg px-3 py-2 flex items-center gap-3">
                <span className="flex-1">{error}</span>
                {lastMsg.current && <button data-testid="kesh-retry-button" onClick={() => send(lastMsg.current)} className="shrink-0 inline-flex items-center gap-1.5 font-semibold text-brand hover:underline"><RefreshCw className="w-3.5 h-3.5" /> Try again</button>}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2 p-3 border-t border-line">
            <textarea ref={inputRef} data-testid="kesh-input" value={input} onChange={(e) => setInput(e.target.value)} rows={1} maxLength={2000}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask Kesh a question…" aria-label="Message Kesh"
              className="flex-1 resize-none max-h-28 bg-surface border border-line-strong/70 rounded-xl px-3 py-2.5 text-base sm:text-[15px] text-ink placeholder:text-faint focus:outline-none focus:border-kesh/60 focus:ring-2 focus:ring-kesh/20" />
            <button data-testid="kesh-send-button" type="submit" disabled={sending || !input.trim()}
              aria-label="Send message" className="w-11 h-11 rounded-xl bg-kesh text-onbrand hover:bg-kesh-hover flex items-center justify-center transition-colors disabled:opacity-40">
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="px-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] text-xs text-faint">Kesh is an AI and can make mistakes. Check important details.</p>
        </div>
      )}
    </>
  );
};
